// TODO_R: add index reexportation here
// TODO_R: leave on this folder only really utility pure functions
// TODO_R: move all api and service functions into src/services/ folder

import { StacksNetwork } from '@stacks/network';
import {
  callReadOnlyFunction,
  ClarityValue,
  contractPrincipalCV,
  cvToJSON,
  uintCV,
} from '@stacks/transactions';
import { validateStacksAddress } from 'micro-stacks/crypto';
import { NftTokenMetadata } from 'types';

import { getNftTokenMetadata } from '../api';
import { NETWORK, NETWORKS } from '../constants/base';

export const getLatestBlockTime = (network: StacksNetwork): Promise<number> => {
  return fetch(`${network.coreApiUrl}/extended/v1/block?limit=1&offset=0`)
    .then((r) => r.json())
    .then((r) => {
      return r.results[0].burn_block_time * 1000;
    });
};

export const getNFTHoldings = (
  network: StacksNetwork,
  principal: string,
  asset_identifiers: string,
  limit = 50,
  offset = 0
): Promise<any> => {
  return fetch(
    `${network.coreApiUrl}/extended/v1/tokens/nft/holdings?principal=${principal}&asset_identifiers=${asset_identifiers}&limit=${limit}&offset=${offset}`
  )
    .then((r) => r.json())
    .then((r) => {
      return r;
    });
};

// export const resolveBns_Name = (network: StacksNetwork, name: string): Promise<string | null> => {
//   return fetch(`${new StacksTestnet().coreApiUrl}/v1/names/${name}`)
//     .then((r) => r.json())
//     .then((r) => {
//       if (r.error) {
//         return null;
//       }

//       return r.address;
//     });
// };

export interface AddressBalance {
  stx: {
    balance: string;
    locked: string;
  };
  fungible_tokens: Record<string, { balance: string }>;
  non_fungible_tokens: Record<string, { count: string }>;
}

const HIRO_API_BASE_URL_MAINNET = 'https://api.hiro.so';
const HIRO_API_BASE_URL_TESTNET = 'https://api.testnet.hiro.so';

// Max page size accepted by the Hiro paginated balance endpoints.
const HIRO_BALANCE_PAGE_LIMIT = 200;

const fetchHiroPaginated = async <T>(
  makeUrl: (limit: number, offset: number) => string
): Promise<T[]> => {
  const items: T[] = [];
  let offset = 0;
  let total = 0;

  do {
    const response = await fetch(makeUrl(HIRO_BALANCE_PAGE_LIMIT, offset));
    const data = (await response.json()) as { total?: number; results?: T[] };
    items.push(...(data.results ?? []));
    total = data.total ?? 0;
    offset += HIRO_BALANCE_PAGE_LIMIT;
  } while (offset < total);

  return items;
};

// Reshapes the Hiro v2 balance endpoints into the AddressBalance shape this app
// expects. The legacy /extended/v1/address/{addr}/balances endpoint is deprecated,
// so STX / FT come from /extended/v2/.../balances/{stx,ft} and NFT counts are
// aggregated from /extended/v1/tokens/nft/holdings (keyed by asset_identifier).
export const getAccountBalances = async (
  network: StacksNetwork,
  account: string
): Promise<AddressBalance> => {
  const basePath =
    network.chainId === StacksNetwork.fromName('mainnet').chainId
      ? HIRO_API_BASE_URL_MAINNET
      : HIRO_API_BASE_URL_TESTNET;

  const [stxResponse, ftItems, nftItems] = await Promise.all([
    fetch(`${basePath}/extended/v2/addresses/${account}/balances/stx`).then(
      (r) => r.json() as Promise<{ balance?: string; locked?: string }>
    ),
    fetchHiroPaginated<{ token: string; balance: string }>(
      (limit, offset) =>
        `${basePath}/extended/v2/addresses/${account}/balances/ft?limit=${limit}&offset=${offset}`
    ),
    fetchHiroPaginated<{ asset_identifier: string }>(
      (limit, offset) =>
        `${basePath}/extended/v1/tokens/nft/holdings?principal=${account}&limit=${limit}&offset=${offset}`
    ),
  ]);

  const fungible_tokens: Record<string, { balance: string }> = {};
  ftItems.forEach((item) => {
    fungible_tokens[item.token] = { balance: item.balance };
  });

  const non_fungible_tokens: Record<string, { count: string }> = {};
  nftItems.forEach((item) => {
    const current = Number(non_fungible_tokens[item.asset_identifier]?.count ?? '0');
    non_fungible_tokens[item.asset_identifier] = { count: (current + 1).toString() };
  });

  return {
    stx: {
      balance: stxResponse?.balance ?? '0',
      locked: stxResponse?.locked ?? '0',
    },
    fungible_tokens,
    non_fungible_tokens,
  };
};

export const getNFTjson = (url: string, id: string, useAltReplace?: boolean) => {
  return fetch(
    (useAltReplace
      ? url.replace('ipfs://ipfs/', 'https://ipfs.io/ipfs/')
      : replaceIPFS(url)
    ).replace('{id}', id)
  )
    .then((r) => r.json())
    .catch((e) => {
      console.error(`Error fetching nft metadata from ${url} with nft id ${id}`, e);

      return null;
    });
};

const callReadOnly = (
  network: StacksNetwork,
  path: string,
  functionArgs: ClarityValue[],
  senderAddress: string
) => {
  const [contractAddress, contractName, functionName] = path.split('.');

  return callReadOnlyFunction({
    contractAddress,
    contractName,
    functionName,
    functionArgs,
    senderAddress,
    network,
  });
};

export const getNfTInfo = async (
  network: StacksNetwork,
  address: string,
  ref: string
): Promise<NftTokenMetadata> => getNftTokenMetadata(address).then((res) => res.result);

export const getNftTokenUri = (
  network: StacksNetwork,
  nftAddress: string,
  nftId: string,
  senderAddress: string
): Promise<string> => {
  return callReadOnly(network, `${nftAddress}.get-token-uri`, [uintCV(nftId)], senderAddress)
    .then((r) => {
      return cvToJSON(r).value.value.value;
    })
    .catch((error) => console.error('Error loading token URI for nft with Id', nftId, error));
};

export const getStacksNetwork = (n: NETWORK): StacksNetwork => {
  return NETWORKS[n];
};

export const contractPrincipalCVFromString = (s: string) => {
  const [a, b] = s.split('.');

  return contractPrincipalCV(a, b);
};

export const isValidRecipient = (r: string) => {
  if (r === '') {
    return false;
  }

  if (validateStacksAddress(r)) {
    return true;
  }

  try {
    contractPrincipalCVFromString(r);

    return true;
  } catch (e) {}

  return false;
};

const replaceIPFS = (url: string) =>
  url.replace('ipfs://', 'https://dweb.link/ipfs/').replace('ar://', 'https://arweave.net/');

export const replaceIPFSimage = (url: string) => {
  return url
    .replace('ipfs://ipfs/', 'https://ipfs.io/ipfs/')
    .replace('ipfs://', 'https://ipfs.io/')
    .replace('ar://', 'https://arweave.net/');
};
